import * as React from 'react';
import { EyeIcon } from 'lucide-react';
import { cn } from '@tinct/core-utils';
import { Card } from '../ui/card';
import { Button } from '../ui/button';

export type BlockListItem = {
  id: string;
  title: string;
  description?: string;
};

export type BlockListItemsCardProps = {
  className?: string;
  contentClassName?: string;
  title?: string;
  items?: BlockListItem[];
  emptyMessage?: string;
  emptyState?: React.ReactNode;
  actionIcon?: React.ReactNode;
  onItemAction?: (item: BlockListItem) => void;
  footerLabel?: string;
  onFooterClick?: () => void;
};

export function BlockListItemsCard({
  className,
  title = 'Active campaigns',
  items = [],
  emptyState,
  actionIcon,
  onItemAction,
  footerLabel,
  onFooterClick,
}: BlockListItemsCardProps) {
  const hasItems = items.length > 0;

  return (
    <Card
      className={cn('overflow-hidden p-6 gap-2 shadow-none', className)}
    >
      {/* Header */}
      <h3 className="text-xl font-semibold tracking-tight text-foreground">
        {title}
      </h3>

      <div className="gap-6 flex flex-col h-full">
        {/* Content */}
        <div className="flex flex-col gap-3 justify-center h-full">
          {hasItems ? (
            items.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-4"
              >
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm font-medium text-foreground">
                    {item.title}
                  </span>
                  {item.description && (
                    <span className="truncate text-sm text-muted-foreground">
                      {item.description}
                    </span>
                  )}
                </div>
                {onItemAction && (
                  <Button
                    variant="outline"
                    size="icon"
                    className="size-9 shrink-0"
                    onClick={() => onItemAction(item)}
                  >
                    {actionIcon || <EyeIcon className="size-4" />}
                  </Button>
                )}
              </div>
            ))
          ) : emptyState }
        </div>

        {/* Footer */}
        {footerLabel && (
          <div className="flex items-center justify-end">
            <Button variant="outline" size="sm" onClick={onFooterClick}>
              {footerLabel}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
